/**
 * Pool admin detail page — tab-per-intent shell.
 *
 * This route owns all data fetching, the shared configuration draft, and
 * every mutating handler; the tab components under
 * src/components/pool-detail/ are presentational consumers. IA and design
 * rationale: .docs/pool-admin-redesign.md.
 *
 *   Overview        — capacity pipeline, scaling status, interventions
 *   Machines        — instances & orchestrators table
 *   Apps            — membership editor (new/legacy auto-pick)
 *   Scaling         — clamps, demand sources, capacity schedule  (config)
 *   Predictive      — forecast charts + predictive knobs         (config)
 *   Infrastructure  — backends editor + provisioning lifecycle   (config)
 *   Activity        — decisions / actions / latency / demand logs
 *   Advanced        — debug inspectors, recovery, migrations
 *
 * Config tabs edit one shared draft (usePoolConfigDraft); a persistent save
 * bar appears while the draft is dirty and one serializer writes the
 * settings JSON.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { usePlatformAdmin } from '../../../hooks';
import { Modal } from '../../../components/shared';
import { GenerateWorkerTokenDialog } from '../../../components/profiles/GenerateWorkerTokenDialog';
import {
  getApiBaseUrl,
  poolUrl,
  regionLabel,
  REFRESH_INTERVAL_MS,
  type AdminPool,
  type BinRow,
  type InfraState,
  type OrchestratorInfo,
  type PoolStats,
  type PredictionRow,
  type ProvisioningState,
  type QuarantineRow,
} from '../../../components/pool-detail/shared';
import { usePoolConfigDraft } from '../../../components/pool-detail/usePoolConfigDraft';
import { OverviewTab } from '../../../components/pool-detail/OverviewTab';
import { MachinesTab } from '../../../components/pool-detail/MachinesTab';
import { AppsTab } from '../../../components/pool-detail/AppsTab';
import { ScalingTab } from '../../../components/pool-detail/ScalingTab';
import { InfrastructureTab } from '../../../components/pool-detail/InfrastructureTab';
import { ActivityTab } from '../../../components/pool-detail/ActivityTab';
import { AdvancedTab } from '../../../components/pool-detail/AdvancedTab';
import { SessionsTab } from '../../../components/pool-detail/SessionsTab';
import styles from './PoolDetail.module.css';

const POOL_TABS = ['overview', 'machines', 'apps', 'sessions', 'scaling', 'infrastructure', 'activity', 'advanced'] as const;
export type PoolTab = typeof POOL_TABS[number];

const TAB_LABELS: Record<PoolTab, string> = {
  overview: 'Overview',
  machines: 'Machines',
  apps: 'Apps',
  sessions: 'Sessions',
  scaling: 'Scaling',
  infrastructure: 'Infrastructure',
  activity: 'Activity',
  advanced: 'Advanced',
};

// Tabs that edit the shared config draft — they carry the unsaved-changes dot.
// Apps holds tenancy; Scaling holds clamps + demand sources (incl. the
// predictive sub-tab); pool name/active are edited inline on the list rows.
const CONFIG_TABS: ReadonlySet<PoolTab> = new Set(['apps', 'scaling', 'infrastructure']);

export const Route = createFileRoute('/admin/pools/$poolId')({
  component: PoolDetailRoute,
  validateSearch: (search: Record<string, unknown>): { tab?: PoolTab } => {
    const tab = search.tab;
    return POOL_TABS.includes(tab as PoolTab) ? { tab: tab as PoolTab } : {};
  },
});

function PoolDetailRoute() {
  const { poolId } = Route.useParams();
  const { tab } = Route.useSearch();
  const activeTab: PoolTab = tab ?? 'overview';
  const { isPlatformAdmin } = usePlatformAdmin();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const setActiveTab = useCallback((next: PoolTab) => {
    navigate({
      to: '/admin/pools/$poolId',
      params: { poolId },
      search: next === 'overview' ? {} : { tab: next },
      replace: true,
    });
  }, [navigate, poolId]);

  // Data state
  const [pool, setPool] = useState<AdminPool | null>(null);
  const poolRef = useRef<AdminPool | null>(null);
  const [stats, setStats] = useState<PoolStats | null>(null);
  const [workers, setWorkers] = useState<OrchestratorInfo[]>([]);
  const [provState, setProvState] = useState<ProvisioningState | null>(null);
  const [infraState, setInfraState] = useState<InfraState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Manual scale form. Desired is edited on the Overview tab; min/max are
  // carried along from the initialization source so the POST body preserves
  // the cloud clamps.
  const [scaleDesired, setScaleDesired] = useState('');
  const [scaleMin, setScaleMin] = useState('');
  const [scaleMax, setScaleMax] = useState('');
  const scaleFormInitialized = useRef(false);

  // Action state
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Predictive observability state (IL-137). Populated by fetchPredictiveData
  // when the pool has predictive (or shadow) enabled.
  const [predictions, setPredictions] = useState<PredictionRow[]>([]);
  const [histogramBins, setHistogramBins] = useState<BinRow[]>([]);
  const [predictiveTimezoneServed, setPredictiveTimezoneServed] = useState<string>('UTC');
  const [quarantine, setQuarantine] = useState<QuarantineRow[]>([]);

  // Provision panel state
  const [provisionResult, setProvisionResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Advanced-tab state
  const [debugData, setDebugData] = useState<unknown>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [desiredData, setDesiredData] = useState<unknown>(null);
  const [desiredLoading, setDesiredLoading] = useState(false);
  const [boardDepthData, setBoardDepthData] = useState<unknown>(null);
  const [boardDepthLoading, setBoardDepthLoading] = useState(false);
  const [boardHistoryData, setBoardHistoryData] = useState<unknown>(null);
  const [boardHistoryLoading, setBoardHistoryLoading] = useState(false);
  const [boardMuxDebugData, setBoardMuxDebugData] = useState<unknown>(null);
  const [boardMuxDebugLoading, setBoardMuxDebugLoading] = useState(false);
  const [rematerializeData, setRematerializeData] = useState<unknown>(null);
  const [rematerializeLoading, setRematerializeLoading] = useState(false);
  const [refreshDesiredStateData, setRefreshDesiredStateData] = useState<unknown>(null);
  const [refreshDesiredStateLoading, setRefreshDesiredStateLoading] = useState(false);

  // Save state for the config draft
  const [editSaving, setEditSaving] = useState(false);
  const [editMessage, setEditMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [infraDefaults, setInfraDefaults] = useState<unknown>(null);

  // Orchestrator releases (for the pinned-version selector on Scaling)
  const [orchestratorReleases, setOrchestratorReleases] = useState<Array<{ version: string; releasedAt: string }>>([]);

  // Token generation dialog
  const [showTokenDialog, setShowTokenDialog] = useState(false);

  // Delete confirmation (delete is only offered while the pool is dormant)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Shared configuration draft — the single edit path for Scaling /
  // Predictive / Infrastructure.
  const draftApi = usePoolConfigDraft(pool);

  // Redirect non-admins
  useEffect(() => {
    if (!isPlatformAdmin) {
      navigate({ to: '/' });
    }
  }, [isPlatformAdmin, navigate]);

  const authHeaders = useCallback(async () => {
    const token = await getToken({ template: 'default' });
    if (!token) throw new Error('Authentication required');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getToken]);

  // Active toggle — same PUT the pool-list rows use, carrying the row's
  // current values so the update never clears a field it didn't touch.
  const handleToggleActive = useCallback(async () => {
    const current = poolRef.current;
    if (!current) return;
    // Deactivation is load-bearing: the pool scales to zero, drains its
    // workers, and goes dormant. Worth a confirm; reactivation is instant.
    if (current.active && !window.confirm(
      `Deactivate "${current.name}"?\n\nThe pool scales to zero, drains its workers, and goes fully dormant — users can no longer start sessions there. Reactivate at any time to resume normal scaling.`,
    )) return;
    setActionInProgress('toggle-active');
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${current.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          name: current.name,
          region: current.region,
          active: !current.active,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      const next = { ...current, active: !current.active };
      setPool(next);
      poolRef.current = next;
    } catch (err: any) {
      setError(err.message || 'Failed to update pool');
    } finally {
      setActionInProgress(null);
    }
  }, [authHeaders]);

  const handleDeletePool = useCallback(async () => {
    setDeleting(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      navigate({ to: '/admin/pools' });
    } catch (err: any) {
      setError(err.message || 'Failed to delete pool');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  }, [authHeaders, poolId, navigate]);

  // ---- Data fetching ----

  const fetchPool = useCallback(async () => {
    const headers = await authHeaders();
    // By-id endpoint: one indexed row instead of fetching the whole pool list
    // just to read this pool's orgId at the head of the load waterfall.
    const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}`, { headers });
    if (!res.ok) throw new Error(`Failed to fetch pool: HTTP ${res.status}`);
    const data = await res.json() as { pool?: AdminPool };
    if (data.pool) {
      setPool(data.pool);
      poolRef.current = data.pool;
    }
  }, [poolId, authHeaders]);

  // Predictive observability fetch. Triple parallel fetch into local state.
  // Failures fall through silently; the surface degrades to "no data"
  // without breaking the page.
  const fetchPredictiveData = useCallback(async () => {
    const headers = await authHeaders();
    try {
      const [predRes, binsRes, quarRes] = await Promise.all([
        fetch(poolUrl(poolId, '/predictive/predictions?limit=200', poolRef.current), { headers }),
        fetch(poolUrl(poolId, '/predictive/bins', poolRef.current), { headers }),
        fetch(poolUrl(poolId, '/predictive/quarantine?limit=50', poolRef.current), { headers }),
      ]);
      if (predRes.ok) {
        const data = await predRes.json() as { predictions: PredictionRow[] };
        setPredictions(data.predictions ?? []);
      }
      if (binsRes.ok) {
        const data = await binsRes.json() as { bins: BinRow[]; timezone: string };
        setHistogramBins(data.bins ?? []);
        setPredictiveTimezoneServed(data.timezone ?? 'UTC');
      }
      if (quarRes.ok) {
        const data = await quarRes.json() as { quarantine: QuarantineRow[] };
        setQuarantine(data.quarantine ?? []);
      }
    } catch {
      /* surface stays as last-known data; auto-refresh will retry */
    }
  }, [poolId, authHeaders]);

  // Seed the predictive histogram from historical session_usage actuals so
  // the 4-week cold-start gate passes immediately (platform-admin POST; see
  // PoolDO.handleSeedPredictive). Returns a status line for the button on
  // the Predictive tab; throws on failure so the button can show the error.
  const seedPredictiveHistory = useCallback(async (): Promise<string> => {
    const headers = await authHeaders();
    const res = await fetch(poolUrl(poolId, '/predictive/seed', poolRef.current), {
      method: 'POST',
      headers: { ...headers, 'content-type': 'application/json' },
      body: JSON.stringify({ weeks: 6 }),
    });
    const data = await res.json() as {
      ok: boolean; error?: string; sessions?: number; busyMinutes?: number;
      distinctWeeks?: number; filledBins?: number; totalBins?: number;
    };
    if (!res.ok || !data.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
    await fetchPredictiveData();
    return `Seeded ${data.sessions} sessions (${data.busyMinutes} busy minutes across ${data.distinctWeeks} weeks) into ${data.filledBins}/${data.totalBins} bins.`;
  }, [poolId, authHeaders, fetchPredictiveData]);

  const fetchRuntimeData = useCallback(async () => {
    const headers = await authHeaders();

    // Fire the four reads INDEPENDENTLY so the page paints on the fast local
    // /stats read instead of waiting on /infrastructure — the one endpoint that
    // makes live GCP-compute + CoreWeave-K8s calls (multi-second, 15s timeout).
    // The render gate is `loading && !stats`, so setting stats first clears it.
    const statsP = fetch(poolUrl(poolId, '/stats', poolRef.current), { headers })
      .then(r => (r.ok ? (r.json() as Promise<PoolStats>) : null))
      .then(d => { if (d) setStats(d); return d; })
      .catch(() => null);

    fetch(poolUrl(poolId, '/orchestrators', poolRef.current), { headers })
      .then(r => (r.ok ? (r.json() as Promise<{ orchestrators: OrchestratorInfo[] }>) : null))
      .then(d => { if (d) setWorkers(d.orchestrators ?? []); })
      .catch(() => {});

    fetch(poolUrl(poolId, '/provisioning-state', poolRef.current), { headers })
      .then(r => (r.ok ? (r.json() as Promise<{ state: ProvisioningState }>) : null))
      .then(d => { if (d) setProvState(d.state); })
      .catch(() => {});

    const infraP = fetch(poolUrl(poolId, '/infrastructure', poolRef.current), { headers })
      .then(r => (r.ok ? (r.json() as Promise<InfraState>) : null))
      .then(d => { if (d) setInfraState({ scaling: d.scaling, health: d.health, instances: d.instances ?? [] }); return d; })
      .catch(() => null);

    // Seed the scale form once from the best available source (needs stats +
    // infra) — off the paint path, so it resolves whenever both land.
    void Promise.all([statsP, infraP]).then(([statsData, infraData]) => {
      if (scaleFormInitialized.current) return;
      scaleFormInitialized.current = true;
      if (statsData?.override?.scaling) {
        setScaleDesired(String(statsData.override.scaling.desiredCapacity));
        setScaleMin(String(statsData.override.scaling.minSize));
        setScaleMax(String(statsData.override.scaling.maxSize));
      } else {
        setScaleDesired(String(infraData?.scaling?.desiredCapacity ?? statsData?.counts?.totalSessionSlots ?? 0));
        setScaleMin(String(infraData?.scaling?.minSize ?? 0));
        setScaleMax(String(infraData?.scaling?.maxSize ?? 10));
      }
    });

    // Resolve as soon as the paint-critical /stats read is in — don't block the
    // caller (loadAll's spinner, predictive) on the slow cloud calls.
    await statsP;
  }, [poolId, authHeaders]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchPool();
      await fetchRuntimeData();
      // Predictive is fetched lazily by its own Scaling-tab effect below, not on
      // every load — only 1 of 8 tabs consumes it.
    } catch (err: any) {
      setError(err.message || 'Failed to load pool data');
    } finally {
      setLoading(false);
    }
  }, [fetchPool, fetchRuntimeData]);

  // Initial load + auto-refresh (paused while the browser tab is hidden).
  useEffect(() => {
    loadAll();

    refreshTimerRef.current = setInterval(() => {
      if (document.hidden) return;
      fetchRuntimeData().catch(() => {});
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [loadAll, fetchRuntimeData]);

  // Predictive data — only the Scaling tab consumes it. Fetch on first open and
  // poll only while it's the active tab (and the browser tab is visible).
  useEffect(() => {
    if (activeTab !== 'scaling') return;
    fetchPredictiveData().catch(() => {});
    const t = setInterval(() => {
      if (document.hidden) return;
      fetchPredictiveData().catch(() => {});
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [activeTab, fetchPredictiveData]);

  // Orchestrator releases for the pinned-version selector — only the Machines
  // tab consumes it, so fetch lazily the first time that tab opens.
  useEffect(() => {
    if (activeTab !== 'machines' || orchestratorReleases.length > 0) return;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${getApiBaseUrl()}/api/admin/orchestrator-releases`, { headers });
        if (res.ok) {
          const data = await res.json() as { releases: Array<{ version: string; releasedAt: string }> };
          setOrchestratorReleases(data.releases ?? []);
        }
      } catch { /* ignore */ }
    })();
  }, [activeTab, orchestratorReleases.length, authHeaders]);

  // Infra defaults — fetched lazily the first time the Infrastructure tab opens
  useEffect(() => {
    if (activeTab !== 'infrastructure' || infraDefaults !== null) return;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${getApiBaseUrl()}/api/admin/pool-infra-defaults`, { headers });
        if (res.ok) {
          const data = await res.json();
          setInfraDefaults((data as any).defaults);
        }
      } catch { /* non-critical */ }
    })();
  }, [activeTab, infraDefaults, authHeaders]);

  // ---- Actions ----

  const handleScale = useCallback(async () => {
    const desired = parseInt(scaleDesired, 10);
    if (isNaN(desired) || desired < 0) return;

    setActionInProgress('scale');
    try {
      const headers = await authHeaders();
      const body: Record<string, number> = { desiredCapacity: desired };
      const min = parseInt(scaleMin, 10);
      const max = parseInt(scaleMax, 10);
      if (!isNaN(min)) body.minSize = min;
      if (!isNaN(max)) body.maxSize = max;

      const res = await fetch(poolUrl(poolId, '/scale', poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Scale request failed');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, scaleDesired, scaleMin, scaleMax, authHeaders, fetchRuntimeData]);

  const handleAddBoost = useCallback(async (slots: number, durationMinutes: number, reason?: string) => {
    if (durationMinutes > 24 * 60) {
      setError('Boost duration cannot exceed 24h. For longer commitments, add a schedule span.');
      return;
    }
    setActionInProgress('boost');
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, '/boost', poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ addedSlots: slots, durationMinutes, reason }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Boost request failed');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleRemoveBoost = useCallback(async (boostId: number) => {
    setActionInProgress(`remove-boost-${boostId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/boost/${boostId}`, poolRef.current), {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel boost');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleAddDampen = useCallback(async (slots: number, durationMinutes: number, reason?: string) => {
    if (durationMinutes > 24 * 60) {
      setError('Dampen duration cannot exceed 24h. For a permanent ceiling, set the pool max clamp.');
      return;
    }
    setActionInProgress('dampen');
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, '/dampen', poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ removedSlots: slots, durationMinutes, reason }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Dampen request failed');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleRemoveDampen = useCallback(async (dampenId: number) => {
    setActionInProgress(`remove-dampen-${dampenId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/dampen/${dampenId}`, poolRef.current), {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel dampen');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handlePauseAutoscaling = useCallback(async () => {
    setActionInProgress('pause');
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, '/override', poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ autoscalingPaused: true, pauseReason: 'admin console' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to pause autoscaling');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleResumeAutoscaling = useCallback(async () => {
    setActionInProgress('resume');
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, '/override', poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'clear' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to resume autoscaling');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleDrainOrchestrator = useCallback(async (orchestratorId: string) => {
    setActionInProgress(`drain-${orchestratorId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/orchestrators/${orchestratorId}/drain`, poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: 'manual' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to drain orchestrator');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleTerminateOrchestrator = useCallback(async (orchestratorId: string) => {
    if (!confirm(`Terminate ${orchestratorId}? This will force-disconnect the orchestrator.`)) return;
    setActionInProgress(`terminate-${orchestratorId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/orchestrators/${orchestratorId}/terminate`, poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: 'evicted' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to terminate orchestrator');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleRebootInstance = useCallback(async (instanceId: string) => {
    if (!confirm(`Reboot instance ${instanceId}? The OS will restart and the orchestrator will reconnect.`)) return;
    setActionInProgress(`reboot-${instanceId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/instances/${instanceId}/reboot`, poolRef.current), {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to reboot instance');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleTerminateInstance = useCallback(async (instanceId: string) => {
    if (!confirm(`Terminate instance ${instanceId}? The pool autoscaler will provision a replacement to keep desired capacity.`)) return;
    setActionInProgress(`terminate-${instanceId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/instances/${instanceId}/terminate`, poolRef.current), {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to terminate instance');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handlePinOrchestrator = useCallback(async (orchestratorId: string, pinned: boolean) => {
    setActionInProgress(`pin-${orchestratorId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/orchestrators/${orchestratorId}/pin`, poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ pinned }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to pin/unpin orchestrator');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleRotateScripts = useCallback(async () => {
    if (!confirm('Rotate startup scripts on all provisioned backends? Only new instances will get the updated scripts.')) return;
    setActionInProgress('rotate-scripts');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/rotate-scripts`, {
        method: 'POST',
        headers,
      });
      const data = await res.json() as any;
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to rotate scripts');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleReprovision = useCallback(async (reset: boolean) => {
    const msg = reset
      ? 'Reset Infrastructure?\n\nAll instances will be terminated, manual overrides cleared, and infrastructure rebuilt from defaults.'
      : 'Refresh Infrastructure?\n\nAll instances will be terminated and infrastructure rebuilt. Manual scale overrides will be re-applied.';
    if (!window.confirm(msg)) return;
    setActionInProgress('provision');
    setProvisionResult(null);
    try {
      const headers = await authHeaders();
      const url = `${getApiBaseUrl()}/api/admin/pools/${poolId}/provision${reset ? '?reset=true' : ''}`;
      const res = await fetch(url, { method: 'POST', headers });
      const data = await res.json() as any;
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setProvisionResult({
        ok: true,
        message: data.queued
          ? `${reset ? 'Reset' : 'Refresh'} accepted — machines are being torn down; rebuild queued behind the cleanup`
          : data.ok ? `${reset ? 'Reset' : 'Refresh'} completed successfully` : `Result: ${JSON.stringify(data)}`,
      });
      fetchRuntimeData();
    } catch (err: any) {
      setProvisionResult({ ok: false, message: err.message || 'Failed to reprovision' });
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleReprovisionBackend = useCallback(async (backendId: string, label: string) => {
    if (!window.confirm(`Reprovision backend ${label}?\n\nInstances on this backend will be terminated and the backend rebuilt. Other backends are not affected.`)) return;
    setActionInProgress(`reprovision-${backendId}`);
    setProvisionResult(null);
    try {
      const headers = await authHeaders();
      const url = `${getApiBaseUrl()}/api/admin/pools/${poolId}/provision/${encodeURIComponent(backendId)}`;
      const res = await fetch(url, { method: 'POST', headers });
      const data = await res.json() as any;
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setProvisionResult({
        ok: true,
        message: `Backend ${label} reprovisioned successfully`,
      });
      fetchRuntimeData();
    } catch (err: any) {
      setProvisionResult({ ok: false, message: `Backend ${label}: ${err.message || 'Failed to reprovision'}` });
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  // ---- Advanced-tab handlers ----

  const handleFetchDebug = useCallback(async (opts?: { includeDownloadUrls?: boolean; bypassCache?: boolean }) => {
    setDebugLoading(true);
    setDebugData(null);
    try {
      const headers = await authHeaders();
      const qs = new URLSearchParams();
      if (opts?.includeDownloadUrls) qs.set('downloadUrls', '1');
      if (opts?.bypassCache) qs.set('bypassCache', '1');
      const query = qs.toString() ? `?${qs.toString()}` : '';
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/debug${query}`, { headers });
      const data = await res.json();
      setDebugData(data);
    } catch (err: any) {
      setDebugData({ error: err.message || 'Failed to fetch debug info' });
    } finally {
      setDebugLoading(false);
    }
  }, [poolId, authHeaders]);

  const handleFetchBoardDepth = useCallback(async () => {
    setBoardDepthLoading(true);
    setBoardDepthData(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/board-depth`, { headers });
      const data = await res.json();
      setBoardDepthData(data);
    } catch (err: any) {
      setBoardDepthData({ error: err.message || 'Failed to fetch board depth' });
    } finally {
      setBoardDepthLoading(false);
    }
  }, [poolId, authHeaders]);

  const handleFetchBoardHistory = useCallback(async () => {
    setBoardHistoryLoading(true);
    setBoardHistoryData(null);
    try {
      const headers = await authHeaders();
      // Default since = now - 5min (matches the backend default).
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/board-history`, { headers });
      const data = await res.json();
      setBoardHistoryData(data);
    } catch (err: any) {
      setBoardHistoryData({ error: err.message || 'Failed to fetch board history' });
    } finally {
      setBoardHistoryLoading(false);
    }
  }, [poolId, authHeaders]);

  const handleFetchBoardMuxDebug = useCallback(async () => {
    setBoardMuxDebugLoading(true);
    setBoardMuxDebugData(null);
    try {
      const headers = await authHeaders();
      // Pool admin needs the orgId to build the mux DO name. by_request
      // pools route through the global mux; everyone else uses the org's.
      if (!pool) {
        setBoardMuxDebugData({ error: 'pool not loaded yet — refresh the page' });
        return;
      }
      const orgId = pool.type === 'by_request' ? 'global' : pool.orgId;
      const res = await fetch(
        `${getApiBaseUrl()}/pool/${orgId}/${poolId}/board-mux/debug`,
        { headers },
      );
      const json = await res.json().catch(() => ({}));
      setBoardMuxDebugData(json);
    } catch (err: any) {
      setBoardMuxDebugData({ error: err.message || 'Failed to fetch board mux debug' });
    } finally {
      setBoardMuxDebugLoading(false);
    }
  }, [pool, poolId, authHeaders]);

  const handleFetchDesiredState = useCallback(async () => {
    setDesiredLoading(true);
    setDesiredData(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/desired-state`, { headers });
      const data = await res.json();
      setDesiredData(data);
    } catch (err: any) {
      setDesiredData({ error: err.message || 'Failed to fetch desired state' });
    } finally {
      setDesiredLoading(false);
    }
  }, [poolId, authHeaders]);

  const handleRematerialize = useCallback(async () => {
    setRematerializeLoading(true);
    setRematerializeData(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/rematerialize`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      setRematerializeData(data);
    } catch (err: any) {
      setRematerializeData({ error: err.message || 'Failed to re-materialize' });
    } finally {
      setRematerializeLoading(false);
    }
  }, [poolId, authHeaders]);

  const handleRefreshDesiredState = useCallback(async () => {
    setRefreshDesiredStateLoading(true);
    setRefreshDesiredStateData(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/refresh-desired-state`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      setRefreshDesiredStateData(data);
    } catch (err: any) {
      setRefreshDesiredStateData({ error: err.message || 'Failed to refresh desired state' });
    } finally {
      setRefreshDesiredStateLoading(false);
    }
  }, [poolId, authHeaders]);

  // ---- Save the config draft ----

  const handleSaveDraft = useCallback(async () => {
    const serialized = draftApi.serialize();
    if (!serialized) return;
    setEditSaving(true);
    setEditMessage(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: serialized.name,
          region: serialized.region,
          settingsJson: serialized.settingsJson || undefined,
          capacityScheduleJson: serialized.capacityScheduleJson || undefined,
          active: serialized.active,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      setEditMessage({ ok: true, text: 'Pool updated' });
      draftApi.markSaved();
      // Refresh pool data (re-hydrates the now-clean draft)
      await fetchPool();
    } catch (err: any) {
      setEditMessage({ ok: false, text: err.message || 'Failed to update pool' });
    } finally {
      setEditSaving(false);
    }
  }, [draftApi, poolId, authHeaders, fetchPool]);

  // ---- Worker token generation (REST-based for admin page) ----

  const generateWorkerToken = useCallback(async (_profileId: string, expiresInSeconds?: number) => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/worker-token`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ expiresInSeconds }),
      });
      const data = await res.json() as any;
      if (!res.ok) return { success: false, error: data.error || `HTTP ${res.status}` };
      return { success: true, token: data.token, expiresInSeconds: data.expiresInSeconds };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to generate token' };
    }
  }, [poolId, authHeaders]);

  if (!isPlatformAdmin) return null;

  if (loading && !stats) {
    return <div className={styles.loading}>Loading pool details...</div>;
  }

  const counts = stats?.counts;

  // Dormant pool: every control except the Active toggle and Delete is
  // inert — the backend refuses mutations on a deactivated pool anyway
  // (PoolDO returns 409), so the UI says so up front instead of letting
  // clicks bounce off the API.
  const dormant = !!pool && !pool.active;

  // Machines-tab count badge: cloud instances plus orchestrators that aren't
  // attached to a listed instance (orphans + external) — matches the row
  // count MachinesTab renders.
  const machineCount = (() => {
    const instances = infraState?.instances ?? [];
    const connected = new Set(instances.map(i => i.orchestratorId).filter(Boolean));
    return instances.length + workers.filter(w => !connected.has(w.workerId)).length;
  })();

  return (
    <div className={styles.container}>
      {/* Sticky header: identity + live status + tabs */}
      <div className={styles.pageHeader}>
        <header className={styles.header} style={{ marginBottom: 0, borderBottom: 'none' }}>
          <div className={styles.headerLeft}>
            <button className={styles.backLink} onClick={() => navigate({ to: '/admin/pools' })}>
              &larr; Pools
            </button>
            <h1 className={styles.poolTitle}>{pool?.name ?? poolId}</h1>
          </div>
          <div className={styles.headerActions}>
            <button
              className={styles.actionButton}
              onClick={() => setShowTokenDialog(true)}
              disabled={dormant}
              title={dormant ? 'Pool is deactivated — reactivate it first' : undefined}
            >
              Generate Token
            </button>
            <button
              className={styles.dangerButton}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={!!pool?.active || actionInProgress === 'toggle-active'}
              title={pool?.active
                ? 'Deactivate the pool first — deactivation scales it to zero and releases its resources'
                : 'Soft-delete this dormant pool (reports keep referencing its id)'}
            >
              Delete
            </button>
            <div className={styles.headerBadges}>
              {pool && (
                <button
                  type="button"
                  className={`${pool.active ? styles.badgeActive : styles.badgeInactive} ${styles.badgeToggle}`}
                  onClick={handleToggleActive}
                  disabled={actionInProgress === 'toggle-active'}
                  aria-pressed={pool.active}
                  aria-busy={actionInProgress === 'toggle-active'}
                  title={pool.active ? 'Deactivate this pool' : 'Activate this pool'}
                >
                  {pool.active ? 'Active' : 'Inactive'}
                </button>
              )}
              {pool && (
                <span className={styles.badgeRegion}>
                  {pool.type === 'by_request' ? 'On-Demand' : pool.type === 'private_pool' ? 'Private' : 'Shared'}
                </span>
              )}
              {pool && <span className={styles.badgeRegion}>{regionLabel(pool.region)}</span>}
            </div>
          </div>
        </header>

        {/* Live status line */}
        <div className={styles.liveStatusLine}>
          {pool && (
            <span className={styles.liveStatusItem} title={`Pool ${pool.id} · Org ${pool.orgId} · Project ${pool.projectId}`}>
              <strong>{pool.orgName}</strong> / {pool.projectName}
            </span>
          )}
          {counts && (
            <span className={styles.liveStatusItem}>
              Slots <strong>{counts.usedSessionSlots}/{counts.totalSessionSlots}</strong>
            </span>
          )}
          {infraState?.scaling && (
            <span className={styles.liveStatusItem}>
              Machines <strong>{infraState.scaling.currentCapacity}/{infraState.scaling.desiredCapacity}</strong>
            </span>
          )}
          {counts && (
            <span className={styles.liveStatusItem}>
              Orchestrators <strong>{counts.total}</strong>
            </span>
          )}
          {stats?.override?.autoscalingPaused && (
            <span className={styles.liveStatusItem} style={{ color: 'var(--color-warning)' }}>
              Autoscaling <strong>paused</strong>
            </span>
          )}
          {provState && (
            <span className={styles.liveStatusItem}>
              Provisioning <strong>{provState.status.replace(/_/g, ' ')}</strong>
            </span>
          )}
        </div>

        {/* Tabs */}
        <nav className={styles.pageTabs} aria-label="Pool sections">
          {POOL_TABS.map(t => {
            const activeSessionCount = workers.reduce((s, w) => s + w.activeJobs.length, 0);
            const label = t === 'machines' && (infraState || workers.length > 0)
              ? `${TAB_LABELS[t]} (${machineCount})`
              : t === 'sessions' && activeSessionCount > 0
              ? `${TAB_LABELS[t]} (${activeSessionCount})`
              : TAB_LABELS[t];
            const showDirty = CONFIG_TABS.has(t) && draftApi.dirty;
            return (
              <button
                key={t}
                type="button"
                className={t === activeTab ? styles.pageTabActive : styles.pageTab}
                aria-current={t === activeTab ? 'page' : undefined}
                aria-label={showDirty ? `${label}, unsaved changes` : undefined}
                onClick={() => setActiveTab(t)}
              >
                {label}
                {showDirty && (
                  <span className={styles.pageTabDirty} title="Unsaved changes" aria-hidden="true">●</span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {dormant && (
        <div className={styles.dormantBanner} role="status">
          <span>Pool deactivated</span>
          <span className={styles.dormantBannerDetail}>
            Dormant — zero capacity, no sessions, controls disabled. Use Inactive (header) to reactivate, or Delete it.
          </span>
        </div>
      )}

      {/* Dormant pools: a disabled fieldset natively inerts every control
          (buttons, inputs, selects) in the config/mutation tabs. Mixed
          tabs (machines, advanced) render outside it and gate only their
          mutating controls via `dormant`, so inspection — refresh, row
          expand, log sub-tabs, debug fetchers — stays live. The Active
          toggle and Delete live in the header, outside all gating. */}
      <fieldset className={styles.tabZone} disabled={dormant}>

      {activeTab === 'overview' && (
        <OverviewTab
          poolId={poolId}
          stats={stats}
          infraState={infraState}
          provState={provState}
          actionInProgress={actionInProgress}
          scaleDesired={scaleDesired}
          onScaleDesiredChange={setScaleDesired}
          onScale={handleScale}
          onBoost={handleAddBoost}
          onRemoveBoost={handleRemoveBoost}
          onDampen={handleAddDampen}
          onRemoveDampen={handleRemoveDampen}
          onPause={handlePauseAutoscaling}
          onResume={handleResumeAutoscaling}
          onReprovisionBackend={handleReprovisionBackend}
        />
      )}

      {activeTab === 'apps' && (
        <AppsTab
          poolId={poolId}
          poolType={pool?.type}
          draftApi={draftApi}
          saving={editSaving}
          authHeaders={authHeaders}
          apiBaseUrl={getApiBaseUrl()}
          onError={msg => setError(msg)}
        />
      )}

      {activeTab === 'scaling' && (
        <ScalingTab
          draftApi={draftApi}
          saving={editSaving}
          stats={stats}
          actionInProgress={actionInProgress}
          onRemoveBoost={handleRemoveBoost}
          onRemoveDampen={handleRemoveDampen}
          predictions={predictions}
          histogramBins={histogramBins}
          quarantine={quarantine}
          predictiveTimezoneServed={predictiveTimezoneServed}
          onSeedHistory={seedPredictiveHistory}
        />
      )}

      {activeTab === 'infrastructure' && (
        <InfrastructureTab
          draftApi={draftApi}
          saving={editSaving}
          infraDefaults={infraDefaults}
          actionInProgress={actionInProgress}
          provisionResult={provisionResult}
          onRotateScripts={handleRotateScripts}
          onReprovision={handleReprovision}
        />
      )}

      </fieldset>

      {activeTab === 'machines' && (
        <MachinesTab
          instances={infraState?.instances ?? []}
          orchestrators={workers}
          draftApi={draftApi}
          saving={editSaving}
          dormant={dormant}
          orchestratorReleases={orchestratorReleases}
          actionInProgress={actionInProgress}
          onDrain={handleDrainOrchestrator}
          onTerminate={handleTerminateOrchestrator}
          onPin={handlePinOrchestrator}
          onReboot={handleRebootInstance}
          onTerminateInstance={handleTerminateInstance}
          onRefresh={() => fetchRuntimeData().catch(() => {})}
          onRefreshDesiredState={handleRefreshDesiredState}
          refreshDesiredStateLoading={refreshDesiredStateLoading}
          refreshDesiredStateData={refreshDesiredStateData}
        />
      )}

      {activeTab === 'sessions' && (
        <SessionsTab
          poolId={poolId}
          workers={workers}
          authHeaders={authHeaders}
          apiBaseUrl={getApiBaseUrl()}
        />
      )}

      {activeTab === 'activity' && (
        <ActivityTab
          stats={stats}
          workers={workers}
          reaper={draftApi.draft ? {
            enabled: draftApi.draft.reaperEnabled,
            idleGraceSeconds: parseInt(draftApi.draft.reaperIdleGraceSeconds, 10) || 720,
            bootImmunitySeconds: parseInt(draftApi.draft.reaperBootImmunitySeconds, 10) || 720,
            maxReapsPerTick: parseInt(draftApi.draft.reaperMaxReapsPerTick, 10) || 5,
          } : null}
        />
      )}

      {activeTab === 'advanced' && (
        <AdvancedTab
          poolId={poolId}
          dormant={dormant}
          debugLoading={debugLoading}
          debugData={debugData}
          onFetchDebug={handleFetchDebug}
          desiredLoading={desiredLoading}
          desiredData={desiredData}
          onFetchDesiredState={handleFetchDesiredState}
          boardDepthLoading={boardDepthLoading}
          boardDepthData={boardDepthData}
          onFetchBoardDepth={handleFetchBoardDepth}
          boardHistoryLoading={boardHistoryLoading}
          boardHistoryData={boardHistoryData}
          onFetchBoardHistory={handleFetchBoardHistory}
          boardMuxDebugLoading={boardMuxDebugLoading}
          boardMuxDebugData={boardMuxDebugData}
          onFetchBoardMuxDebug={handleFetchBoardMuxDebug}
          rematerializeLoading={rematerializeLoading}
          rematerializeData={rematerializeData}
          onRematerialize={handleRematerialize}
        />
      )}

      {/* Persistent save bar for the shared config draft. Wrapped in its
          own dormant-gated fieldset: a draft left dirty at deactivation
          must not be saveable (or discardable) until reactivation. */}
      <fieldset className={styles.tabZone} disabled={dormant}>
      {draftApi.dirty && (
        <div className={styles.saveBar}>
          <span className={styles.saveBarText}>
            Unsaved configuration changes
            {editMessage && !editMessage.ok && (
              <span style={{ color: 'var(--color-error)', marginLeft: 'var(--space-3)', textTransform: 'none' }}>
                {editMessage.text}
              </span>
            )}
          </span>
          <div className={styles.saveBarActions}>
            <button
              className={styles.actionButton}
              onClick={() => { draftApi.discard(); setEditMessage(null); }}
              disabled={editSaving}
            >
              Discard
            </button>
            <button
              className={styles.applyButton}
              onClick={handleSaveDraft}
              disabled={editSaving || !draftApi.draft?.name.trim()}
            >
              {editSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
      {!draftApi.dirty && editMessage?.ok && CONFIG_TABS.has(activeTab) && (
        <div style={{
          marginTop: 'var(--space-3)',
          padding: 'var(--space-2) var(--space-3)',
          fontSize: 'var(--text-xs)',
          border: 'var(--border-width) solid var(--text-primary)',
          color: 'var(--text-primary)',
        }}>
          {editMessage.text}
        </div>
      )}

      </fieldset>

      {/* Delete Confirmation */}
      {showDeleteConfirm && pool && (
        <Modal
          isOpen={true}
          title="Delete Pool"
          subtitle={`Delete "${pool.name}"? This is a soft delete — reports and analytics keep referencing its id, and it disappears from the pool list.`}
          onClose={() => setShowDeleteConfirm(false)}
          size="small"
        >
          <div className={styles.confirmActions}>
            <button
              className={styles.actionButton}
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              className={styles.dangerButton}
              onClick={handleDeletePool}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}

      {/* Worker Token Dialog */}
      {showTokenDialog && pool && (
        <GenerateWorkerTokenDialog
          profileId={pool.id}
          profileName={pool.name}
          onClose={() => setShowTokenDialog(false)}
          generateWorkerToken={generateWorkerToken}
        />
      )}
    </div>
  );
}
